import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    calendarDays: Array<{
        date: Date;
        day: number;
        isCurrentMonth: boolean;
        isToday: boolean;
        isSelected: boolean;
        isDisabled: boolean;
    }>;
    onDateClick: (day: { date: Date; isDisabled: boolean }) => void;
    focusedDayIndex: number;
    onFocusedDayChange?: (index: number) => void;
}

export function DaysView({ calendarDays, onDateClick, focusedDayIndex, onFocusedDayChange }: Props) {
    const t = useTranslations();
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const dayNames = useMemo(() => {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return days.map(day => t(`general.datePicker.days.${day.toLowerCase()}`, { defaultValue: day }));
    }, [t]);

    const handleDayClick = useCallback(
        (day: (typeof calendarDays)[0], index: number) => {
            onDateClick({ date: day.date, isDisabled: day.isDisabled });
            onFocusedDayChange?.(index);
        },
        [onDateClick, onFocusedDayChange],
    );

    const focusDay = useCallback((index: number) => {
        const button = buttonRefs.current[index];
        if (button && !button.disabled) {
            button.focus();
            return;
        }
        buttonRefs.current.find(button => button != null && !button.disabled)?.focus();
    }, []);

    useEffect(() => {
        if (focusedDayIndex >= 0 && focusedDayIndex < buttonRefs.current.length) {
            focusDay(focusedDayIndex);
        }
    }, [focusedDayIndex, focusDay]);

    useChangeEffect(() => {
        const id = requestAnimationFrame(() => focusDay(focusedDayIndex));
        return () => cancelAnimationFrame(id);
    }, focusDay);

    return (
        <>
            <div className="mb-1 grid grid-cols-7 text-center text-sm text-neutral-500 md:text-xs">
                {dayNames.map((day, index) => (
                    <div key={index} className="py-1 md:py-1">
                        {day}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 justify-items-center gap-1 md:h-auto" role="grid">
                {calendarDays.map((day, index) => (
                    <button
                        key={index}
                        ref={el => {
                            buttonRefs.current[index] = el;
                        }}
                        type="button"
                        role="gridcell"
                        tabIndex={index === focusedDayIndex ? 0 : -1}
                        className={twJoin(
                            'w-full rounded-md py-3 text-md md:h-6 md:w-7 md:py-0 md:text-sm',
                            !day.isCurrentMonth && 'text-neutral-300',
                            day.isCurrentMonth && !day.isSelected && !day.isToday && 'hover:bg-neutral-100',
                            day.isToday && !day.isSelected && 'border border-primary-500',
                            day.isSelected && 'bg-primary-500 text-white',
                            day.isDisabled && 'cursor-not-allowed opacity-50',
                        )}
                        onClick={() => handleDayClick(day, index)}
                        disabled={day.isDisabled}
                        aria-disabled={day.isDisabled}
                        aria-label={day.date.toDateString()}
                        aria-selected={day.isSelected || undefined}>
                        {day.day}
                    </button>
                ))}
            </div>
        </>
    );
}
